import jsPDF from 'jspdf'
import 'jspdf-autotable'
import moment from 'moment'
import { INSURANCE, LOGISTICS, SMARTBOARD, TRUCK_SALES } from '../../constants/divisions'
import { formatDateWithTimezone } from '../../utils/utcTime'

const getCommonColumns = (divisionId) => {
  const baseColumns = ['name', 'calls']

  if (divisionId === LOGISTICS) {
    baseColumns.push('long calls')
  }

  baseColumns.push('talk time', 'talk time %', 'incoming calls', 'incoming calls talk time')

  return baseColumns
}

export const formatDuration = (duration) => {
  if (!duration) return '00:00:00'

  const normalizedTime = (time) => {
    if (time < 10) return `0${time}`
    else return time
  }

  const hours = Math.floor(duration / 3600)
  const minutes = Math.floor((duration % 3600) / 60)
  const seconds = Math.floor((duration % 3600) % 60)

  return `${normalizedTime(hours)}:${normalizedTime(minutes)}:${normalizedTime(seconds)}`
}

export const getDateRangeByValue = (value) => {
  let from = null
  let to = null
  switch (value) {
    case 'today':
      from = moment().format('YYYY-MM-DD')
      to = moment().format('YYYY-MM-DD')
      break
    case 'yesterday':
      from = moment().subtract(1, 'day').format('YYYY-MM-DD')
      to = moment().subtract(1, 'day').format('YYYY-MM-DD')
      break
    case 'last7days':
      from = moment().subtract(7, 'day').format('YYYY-MM-DD')
      to = moment().subtract(1, 'day').format('YYYY-MM-DD')
      break

    case 'last4weeks':
      from = moment().subtract(4, 'weeks').startOf('isoWeek').format('YYYY-MM-DD')
      to = moment().subtract(1, 'weeks').endOf('isoWeek').format('YYYY-MM-DD')
      break
    case 'last30days':
      from = moment().subtract(30, 'day').format('YYYY-MM-DD')
      to = moment().subtract(1, 'day').format('YYYY-MM-DD')
      break
    case 'thisMonth':
      from = moment().startOf('month').format('YYYY-MM-DD')
      to = moment().endOf('month').format('YYYY-MM-DD')
      break
    case 'lastMonth':
      from = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
      to = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
      break
    default:
      break
  }

  return { from, to }
}

export const exportSalesMetricReportToPdf = (dataSource, { division = {}, dateRange = {} }, callback = () => {}) => {
  const unit = 'pt'
  const size = 'A4' // Use A1, A2, A3 or A4
  const orientation = 'landscape' // portrait or landscape

  const marginLeft = 40
  const doc = new jsPDF(orientation, unit, size)

  doc.setFontSize(15)

  const title = `${division.name} Sales Metrics Report ${
    dateRange.from && moment(dateRange.from).format('MM/DD/YYYY')
  }${dateRange.to && ` - ${moment(dateRange.to).format('MM/DD/YYYY')}`}`

  const columns = [
    { header: 'Name', dataKey: 'name' },
    { header: 'Calls', dataKey: 'calls' },
  ]

  // Add Long Calls column for LOGISTICS division
  if (LOGISTICS === division.id) {
    columns.push({ header: 'Long Calls', dataKey: 'longCalls' })
  }

  columns.push(
    { header: 'Talk Time', dataKey: 'callTime' },
    { header: 'Talk Time %', dataKey: 'callTimePercentage' },
    { header: 'Incoming Calls', dataKey: 'incomingCalls' },
    { header: 'Incoming Calls Talk Time', dataKey: 'incomingCallTime' },
    { header: 'Visits', dataKey: 'meetings' },
    {
      header: LOGISTICS === division.id ? 'Weekly / Monthly / YTD / Total Prospects' : 'New / Total Prospects',
      dataKey: 'prospects',
    }
  )

  const targetAccountsOrTrucksSoldIndex =
    columns.findIndex(
      (col) => col.header == 'Weekly / Monthly / YTD / Total Prospects' || col.header == 'New / Total Prospects'
    ) + 1
  if (TRUCK_SALES !== division.id) {
    columns.splice(targetAccountsOrTrucksSoldIndex, 0, {
      header: LOGISTICS === division.id ? 'Weekly / Monthly / YTD / Total Accounts' : 'New / YTD / TTM Accounts',
      dataKey: 'accounts',
    })
  } else {
    columns.splice(targetAccountsOrTrucksSoldIndex, 0, {
      header: 'New / YTD / TTM Trucks Sold',
      dataKey: 'trucksSold',
    })
  }

  if (LOGISTICS === division.id) {
    const targetIndex = columns.findIndex((col) => col.header == 'Weekly / Monthly / YTD / Total Prospects') + 1

    columns.splice(
      targetIndex,
      0,
      { header: 'Weekly / Monthly / YTD / Total RFP Prospects', dataKey: 'rfpProspects' },
      { header: 'Weekly / Monthly / YTD / Total MF', dataKey: 'mf' },
      { header: 'Weekly / Monthly / YTD / Total Credit App', dataKey: 'creditApp' },
      { header: 'Weekly / Monthly / YTD / Total Online Form', dataKey: 'onlineForm' },
      { header: 'Weekly / Monthly / YTD / Total Pending Accounts', dataKey: 'pendingAccounts' }
    )
  }

  if (SMARTBOARD === division.id) {
    columns.splice(getCommonColumns(division.id).length, 0, { header: 'Demos', dataKey: 'demos' })
  }

  // Add SMS column for INSURANCE division only
  if (INSURANCE === division.id) {
    const targetIndex = columns.findIndex((col) => col.header === 'Incoming Calls Talk Time') + 1

    columns.splice(targetIndex, 0, { header: 'In / Out / Total SMS', dataKey: 'smsMessages' })
  }

  const customColumn = (columnData, columnName) => {
    if (columnData.column.dataKey === columnName) {
      if (columnData.row.section === 'body') {
        if (columnData.cell.raw.color !== 'transparent') {
          columnData.cell.styles.fillColor = columnData.cell.raw.color
        }
        columnData.cell.text = columnData.cell.raw.value
      }
    }
  }

  const withDetails = (value, defaultValue) => {
    if (!value) {
      return defaultValue
    }
    return value
  }

  const data = dataSource.map((d) => {
    const time = formatDuration(d.duration)
    const incomingTime = formatDuration(d.incomingCallsDuration || 0)
    const allIncomingTime = formatDuration(d.allIncomingCallsDuration || d.all_incoming_calls_duration || 0)
    const allIncomingCallsCount = d.allIncomingCallsCount || d.all_incoming_calls_count || 0

    let row = {
      name: `${d.userData.name} ${d.userData?.empStartDate && `(${formatDateWithTimezone(d.userData.empStartDate)})`}`,
      calls: { value: withDetails(d.calls, '0'), color: d.callsColor },
      callTime: { value: withDetails(time, '0'), color: d.talkTimeColor },
      callTimePercentage: { value: withDetails(d.percentage, '0'), color: d.talkTimeColor },
      incomingCalls: {
        value: `${withDetails(d.incomingCalls, '0')} / ${withDetails(allIncomingCallsCount, '0')}`,
        color: d.callsColor,
      },
      incomingCallTime: {
        value: `${withDetails(incomingTime, '0')} / ${withDetails(allIncomingTime, '0')}`,
        color: d.talkTimeColor,
      },
      meetings: { value: withDetails(d.meetingsCount, '0'), color: d.meetingColor },
    }

    // Add SMS data for INSURANCE division only
    if (INSURANCE === division.id) {
      row.smsMessages = {
        value: `${withDetails(d.incomingSmsCount, '0')} / ${withDetails(d.outgoingSmsCount, '0')} / ${withDetails(
          d.totalSmsCount,
          '0'
        )}`,
        color: 'transparent',
      }
    }

    // For LOGISTICS: Weekly / Monthly / YTD / Total
    if (LOGISTICS === division.id) {
      row.prospects = {
        value: withDetails(
          `${d.weeklyProspects} / ${d.monthlyProspects} / ${d.ytdProspects} / ${d.totalProspects}`,
          '0'
        ),
        color: d.prospectColor,
      }
      row.accounts = {
        value: withDetails(`${d.weeklyAccounts} / ${d.monthlyAccounts} / ${d.ytdAccounts} / ${d.totalAccounts}`, '0'),
        color: d.accountColor,
      }
    } else {
      // For other divisions: New / Total for prospects, New / YTD / TTM for accounts
      row.prospects = {
        value: withDetails(`${d.newProspects} / ${d.totalProspects}`, '0'),
        color: d.prospectColor,
      }
      row.accounts = {
        value: withDetails(`${d.newAccounts} / ${d.ytdAccounts} / ${d.ttmAccounts}`, '0'),
        color: d.accountColor,
      }
    }

    // Add Long Calls for LOGISTICS division
    if (LOGISTICS === division.id) {
      row.longCalls = { value: withDetails(d.longCalls, '0'), color: d.callsColor }
    }

    if (TRUCK_SALES === division.id) {
      row = {
        ...row,
        trucksSold: {
          value: withDetails(`${d.newTrucksSold} / ${d.ytdTrucksSold} / ${d.ttmTrucksSold}`, '0'),
          color: d.accountColor,
        },
      }
    }

    if (LOGISTICS === division.id) {
      row = {
        ...row,
        rfpProspects: {
          value: withDetails(
            `${d.weeklyRfpProspects} / ${d.monthlyRfpProspects} / ${d.ytdRfpProspects} / ${d.totalRfpProspects}`,
            '0'
          ),
          color: d.prospectColor,
        },
        mf: {
          value: withDetails(`${d.weeklyMf} / ${d.monthlyMf} / ${d.ytdMf} / ${d.totalMf}`, '0'),
          color: d.prospectColor,
        },
        creditApp: {
          value: withDetails(
            `${d.weeklyCreditApp} / ${d.monthlyCreditApp} / ${d.ytdCreditApp} / ${d.totalCreditApp}`,
            '0'
          ),
          color: d.prospectColor,
        },
        pendingAccounts: {
          value: withDetails(
            `${d.weeklyPendingAccounts} / ${d.monthlyPendingAccounts} / ${d.ytdPendingAccounts} / ${d.totalPendingAccounts}`,
            '0'
          ),
          color: d.accountColor,
        },
        onlineForm: {
          value: withDetails(
            `${d.weeklyOnlineForm} / ${d.monthlyOnlineForm} / ${d.ytdOnlineForm} / ${d.totalOnlineForm}`,
            '0'
          ),
          color: d.prospectColor,
        },
      }
    }

    if (SMARTBOARD === division.id) {
      row = { ...row, demos: { value: withDetails(d.demosCount, '0'), color: d.demoColor } }
    }

    return row
  })

  let content = {
    startY: 50,
    columns,
    headStyles: { fillColor: [250, 183, 16] },
    styles: { halign: 'center', lineColor: [255, 255, 255], lineWidth: 1 },
    body: data,
    didParseCell: (data) => {
      customColumn(data, 'calls')
      customColumn(data, 'longCalls')
      customColumn(data, 'callTime')
      customColumn(data, 'callTimePercentage')
      customColumn(data, 'incomingCalls')
      customColumn(data, 'incomingCallTime')
      customColumn(data, 'smsMessages')
      customColumn(data, 'meetings')
      customColumn(data, 'demos')
      customColumn(data, 'prospects')
      customColumn(data, 'rfpProspects')
      customColumn(data, 'creditApp')
      customColumn(data, 'pendingAccounts')
      customColumn(data, 'accounts')
      customColumn(data, 'trucksSold')
      customColumn(data, 'mf')
      customColumn(data, 'onlineForm')
    },
  }

  doc.text(title, marginLeft, 40)
  doc.autoTable(content)
  doc.save(`${title}.pdf`)

  if (callback && typeof callback === 'function') {
    callback()
  }
}
